import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { DatabaseIcon, ShieldIcon, SparklesIcon, ShieldCheckIcon } from 'lucide-react';
const pillars = [{
  icon: DatabaseIcon,
  label: 'Meten voordat we bouwen',
  desc: <>
        We stellen eerst met{' '}
        <strong className="font-semibold text-brand-dark" data-id="element-342" data-mp="1vcsoy9db3jfj">
          echte advertentiedata
        </strong>{' '}
        vast welke zoekopdrachten aanvragen opleveren. Pas daarna wordt er
        organische structuur op gebouwd.
      </>
}, {
  icon: SparklesIcon,
  label: 'Vindbaar op meer dan Google alleen',
  desc: <>
        De structuur wordt zo opgezet dat je ook zichtbaar bent in{' '}
        <strong className="font-semibold text-brand-dark" data-id="element-343" data-mp="2f2cv1c20edjv">
          AI-gedreven zoekmachines
        </strong>
        , waar een groeiend deel van de zakelijke zoekopdrachten naartoe
        verschuift.
      </>
}, {
  icon: ShieldCheckIcon,
  label: 'De data is van jou',
  desc: <>
        Het CorePrint-rapport en de onderliggende campagnedata blijven{' '}
        <strong className="font-semibold text-brand-dark" data-id="element-344" data-mp="303jdhy8yan59">jouw eigendom</strong>
        . Ook als je het traject beëindigt of zelf verder wilt.
      </>
}, {
  icon: ShieldIcon,
  label: 'Marktexclusiviteit bij Platinum',
  desc: <>
        Bij het{' '}
        <strong className="font-semibold text-brand-dark" data-id="element-345" data-mp="3erxcfy38xrgj">
          Platinum-pakket
        </strong>{' '}
        werken we in jouw markt en regio niet ook voor je directe concurrent. Bij
        de andere pakketten geldt die afspraak niet.
      </>
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.23, 1, 0.32, 1]
    }
  }
};
export function WhatIs() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-20 md:py-24 relative" style={{
    background: 'linear-gradient(180deg, #ffffff 0%, #F8FAFC 100%)'
  }} data-id="element-346" data-mp="1mco11qm6x0j6">
      <div className="max-w-[1200px] mx-auto px-6 relative z-10" data-id="element-347" data-mp="1vahyj4voy07x">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-start" data-id="element-348" data-mp="29b2frwlyjnzb">
          {/* Left */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.55
        }} data-id="element-349" data-mp="259ywf04pb9tc">
            <div className="section-label" data-id="element-350" data-mp="2sx757s1c9104">Wat CorePrint is</div>
            <h2 className="text-[clamp(2rem,3.6vw,2.8rem)] font-bold text-brand-dark leading-tight tracking-tight mb-6" data-id="element-351" data-mp="2o1i0laagotwy">
              Een meetinstrument dat de investering stuurt
            </h2>
            <p className="text-brand-gray leading-relaxed text-[1.05rem] mb-5" data-id="element-352" data-mp="3bmk8hn5g5uwm">
              De meeste online trajecten beginnen bij het bouwen. Er komen
              pagina&apos;s, er komt structuur, en de zoekwoorden daarvoor komen
              uit een tool die zoekvolume toont. Zoekvolume zegt hoeveel mensen
              iets intypen. Het zegt niets over de vraag of die mensen klant
              worden.
            </p>
            <p className="text-brand-gray leading-relaxed text-[1.05rem] mb-5" data-id="element-353" data-mp="2squ824d5x6ku">
              CorePrint draait die volgorde om. We zetten negentig dagen een
              gerichte advertentiecampagne in als sonar: een meetinstrument dat
              per zoekopdracht registreert wat er gebeurt. Klikken, aanvragen,
              kosten per aanvraag. Niet landelijk gemiddeld, maar in jouw markt.
            </p>
            <p className="text-brand-gray leading-relaxed text-[1.05rem] mb-10" data-id="element-354" data-mp="2a02pqdb765ub">
              Na die negentig dagen ligt er een rapport met de zoekopdrachten die
              bewezen converteren, en met de zoekopdrachten die volume hadden
              maar niets opleverden. Dat rapport bepaalt waar we bouwen. Naarmate
              de organische posities groeien, kan het advertentiebudget omlaag.
            </p>
            <a href="#gesprek" className="inline-flex items-center bg-brand-primary text-white font-semibold px-7 py-4 rounded-lg hover:opacity-90 transition-opacity duration-200" data-id="element-355" data-mp="2tkwrsz7bgtrr">
              Plan een presentatie
            </a>
          </motion.div>

          {/* Right: pillars */}
          <motion.div className="grid sm:grid-cols-2 gap-5 items-stretch" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-356" data-mp="2fmf594t5dnbf">
            {pillars.map(p => <motion.div key={p.label} className="rounded-2xl p-7 border border-[#E8EDF8] bg-white flex flex-col" variants={itemVariants} data-id="element-357" data-mp="ldjh971dxwid">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{
              background: '#EFF6FF',
              border: '1px solid rgba(0, 82, 204, 0.15)'
            }} data-id="element-358" data-mp="v3cuech6ejoq">
                  <p.icon size={20} className="text-brand-primary" data-id="element-359" data-mp="2h1r2u26q4o8" />
                </div>
                <div className="font-bold text-brand-dark text-[1rem] mb-3" data-id="element-360" data-mp="7fuwdz7bipdb">
                  {p.label}
                </div>
                <div className="text-brand-gray text-[0.9rem] leading-relaxed" data-id="element-361" data-mp="hboh45v6xp66">
                  {p.desc}
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}